import { useMemo } from "react"
import { DiscussionResponseFragment, GroupStatus } from "@pathwright/graphql-client/src/generated"
import useCohortPermissions, {
  useCohortPermissionsFragment
} from "../../cohort/useCohortPermissions"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"

export type DiscussionResponsePermissions = {
  canCreate: boolean
  canUpdate: boolean
  canDelete: boolean
  canVote: boolean
  canAskToAnswer: boolean
  canViewFullName: boolean
}

// Optionally provide response for scoping permissions to specific response.
// Note that we are not considering step-scoped discussion which would
// require factoring in a user's mentor group.
function useDiscussionResponsePermissions(
  cohortId: number | null,
  response?: DiscussionResponseFragment | null
): DiscussionResponsePermissions {
  const { me } = usePathwrightContext()

  const cohort = useCohortPermissionsFragment(cohortId!)

  // Check if the cohort is complete.
  const cohortIsComplete = cohort?.status === GroupStatus.Completed
  // Check if user registration is soft-archived.
  const userRegistrationIsSoftArchived =
    !!cohort?.user_registration?.softArchivedTime

  const cohortPerms = useCohortPermissions({
    cohortId
  })

  const responseUserId = response?.user?.id
  const meId = me?.id

  return useMemo(() => {
    // Define narrowed base permissions that may be broadened below based on user permissions.
    const perms: DiscussionResponsePermissions = {
      canCreate: false,
      canUpdate: false,
      canDelete: false,
      canVote: false,
      canAskToAnswer: false,
      canViewFullName: false
    }

    // Authors can update/delete their own responses.
    if (responseUserId === meId) {
      perms.canUpdate = true
      perms.canDelete = true
    }

    // Learners+ can create responses and vote on responses.
    if (cohortPerms.hasLearnerLevelAccess) {
      perms.canCreate = true
      perms.canVote = true
    }

    // Moderators+ can set update/delete any response.
    if (cohortPerms.hasModeratorLevelAccess) {
      perms.canCreate = true
      perms.canUpdate = true
      perms.canDelete = true
      perms.canVote = true
      perms.canAskToAnswer = true
      perms.canViewFullName = true
    } else {
      // When user does not have editor access, they may be restricted to the
      // base permissions depending on cohort and registration status.
      if (cohortIsComplete || userRegistrationIsSoftArchived) {
        perms.canCreate = false
        perms.canUpdate = false
        perms.canDelete = false
        perms.canVote = false
        perms.canAskToAnswer = false
      }
    }

    return perms
  }, [
    responseUserId,
    meId,
    cohortPerms,
    cohortIsComplete,
    userRegistrationIsSoftArchived
  ])
}

export default useDiscussionResponsePermissions
